import { g, l } from './';
import {
	addQueryArg,
	currentTime,
	clearOmCookiesStorage,
	queryArgs,
	referrer,
	storageAvailable,
	trigger,
	urlPath,
} from './Helpers';

/**
 * Manages has visited page tracking for the app.
 *
 * @since 5.0.0
 */
export default class Visits {
	/**
	 * The key used for storing the first visit object.
	 *
	 * @since 5.0.0
	 */
	static firstVisitKey = 'omVisitsFirst';

	/**
	 * The key used for storing the visits information array.
	 *
	 * @since 5.0.0
	 */
	static visitsKey = 'omVisits';

	/**
	 * "Class" constructor in ES6.
	 *
	 * @since 5.0.0
	 *
	 * @param {Object} defaults the default data set for this instance of Api.js
	 */
	constructor() {
		// If we've already tracked the visit, don't run `init()`.
		if (window[g.GLOBAL_OM].visitsAdded) {
			return;
		}

		// Set the maximum number of visits to store in `omVisits` local storage key.
		this.maxVisits = 500;

		// Initialize!
		this.init();
	}

	/**
	 * A default method to initialize the class.
	 *
	 * @since 5.0.0
	 *
	 * @returns {void}
	 */
	init() {
		trigger(document, 'Visits.init', {
			Visits: this,
		});

		// Log that we've tracked this visit.
		window[g.GLOBAL_OM].visitsAdded = true;

		// If localStorage is not available,
		if (!storageAvailable('localStorage')) {
			l.debug('Local Storage not available in this browser.');
			return;
		}

		// Ok, if someone wants to clear sessions, we will do that now.
		if (clearOmCookiesStorage()) {
			Visits.deleteAll();
		}

		// Add the new visit.
		this.add();
	}

	/**
	 * Add the current visit to `localStorage`.
	 *
	 * @since 5.0.0
	 *
	 * @returns {void}
	 */
	add = () => {
		// Set up some initial variables.
		let storage = null;
		let first = null;
		let visits = null;

		try {
			// Get the local storage object.
			storage = window.localStorage;

			// Get the first visit, and visits array from local storage.
			first = JSON.parse(storage.getItem(Visits.firstVisitKey) || '{}');
			visits = JSON.parse(storage.getItem(Visits.visitsKey) || '[]');

			// Remove the items from storage. Now that we limit the number of
			// storage items, this is an attempt to recover space taken up by
			// large, visit-related, storage items that are preventing new items
			// from being set in local storage.
			// storage.removeItem(Visits.firstVisitKey)
			storage.removeItem(Visits.visitsKey);
		} catch {
			return;
		}

		// Bail if `localStorage` is unavailable.
		if (!storageAvailable('localStorage')) {
			return;
		}

		// Set up some more variables.
		const noVisits = 1 > this.maxVisits;
		const visit = {
			path: urlPath(),
			queryArgs: queryArgs(),
			timestamp: Math.floor(currentTime() / 1000),
			referrer: referrer(),
		};

		// If this is the first visit, store as object under `omVisitedFirst`.
		// Otherwise, append to the `omVisited` array in localStorage.
		if ('undefined' === typeof first.path && 'undefined' === typeof first.timestamp) {
			storage.setItem(Visits.firstVisitKey, JSON.stringify(visit));
		} else {
			// If the visits array is already empty, and the maximum number of stored
			// visits is less than one (likely zero), no further action is needed.
			// The customer is telling us not to store any visits.
			if (!visits.length && noVisits) {
				return;
			}

			// We've reached the maximum number of visits, so we need to trim the
			// array before adding the newest visit.
			if (visits.length >= this.maxVisits) {
				// If the maximum number of visits less than one (likely zero),
				// let's set the `omVisits` to an empty array, and bail.
				if (noVisits) {
					storage.setItem(Visits.visitsKey, JSON.stringify([]));
					return;
				}

				// Find the number of excess visits, and reduce the array length to
				// max visits + 1. e.g. - If the maximum number of visits to store
				// is 500, we need to reduce the array length to 499. We do this
				// by setting the starting slice point to the array length minus
				// the maximum number of stored visits plus one. Using the prior
				// example, if the number of currently stored visits is 600, we
				// will need to remove 101 visits from the beginning of the array,
				// which will leave us with 499 visits. In the lines below, we
				// will push the current visit onto the end of the array for a
				// total of 500 visits.
				visits = visits.slice(visits.length - this.maxVisits + 1);
			}

			// Add the newest visit object to the visits array.
			visits.push(visit);

			try {
				// Attempt to, safely, store the visits array in local storage.
				// Since, this could be a large string, there's a much higher
				// change this one item could throw a "quota exceeded" error. We
				// want to make sure this error isn't surfaced, so we swallow
				// the errors, which results in no visits being set.
				storage.setItem(Visits.visitsKey, JSON.stringify(visits));
			} catch {
				try {
					// We couldn't store the full array of visits. Here, we
					// attempt to save the current visit.
					storage.setItem(Visits.visitsKey, JSON.stringify([visit]));
				} catch {
					// No visits could be stored, so we do nothing.
				}
			}
		}
	};

	/**
	 * Get the specified item from `localStorage`.
	 *
	 * We don't check for the availability of storage here. Since we are not
	 * writing, try/catch is sufficient.
	 *
	 * @since 5.0.0
	 *
	 * @param {string} item Whether or not to retrieve the `omVisitedFirst` key.
	 * @returns {Array|Object|null} The `localStorage` key value.
	 */
	static get = (item) => {
		try {
			let storage = window.localStorage;
			let defaultValue = item === Visits.firstVisitKey ? '{}' : '[]';
			let value = JSON.parse(storage.getItem(item) || defaultValue);

			return value;
		} catch (e) {
			return null;
		}
	};

	/**
	 * Returns all visited objects, with the first visit prepended to the
	 * visited array.
	 *
	 * @since 5.0.0
	 *
	 * @returns {Array} Add array of
	 */
	static getAll = () => {
		try {
			let firstVisit = Visits.get(Visits.firstVisitKey);
			let visits = Visits.get(Visits.visitsKey);

			visits.unshift(firstVisit);

			return visits;
		} catch (e) {
			return [];
		}
	};

	/**
	 * Delete the specified `localStorage` key.
	 *
	 * We don't check for the availability of storage here. Since we are not
	 * writing, try/catch is sufficient.
	 *
	 * @since 5.0.0
	 *
	 * @param {string} key The `localStorage` key to delete.
	 * @returns {void}
	 */
	static deleteAll = () => {
		try {
			let storage = window.localStorage;
			storage.removeItem(Visits.firstVisitKey);
			storage.removeItem(Visits.visitsKey);
		} catch (e) {}
	};

	/**
	 * Get the URL for a visit. Combines path and query args.
	 *
	 * @since  5.19.59
	 *
	 * @param  {Object} visit Visit object.
	 *
	 * @returns {string}       The URL for the visit.
	 */
	static getUrl = (visit) => {
		let url = visit.path;

		if (visit.queryArgs && Object.keys(visit.queryArgs).length) {
			for (let arg in visit.queryArgs) {
				url = addQueryArg(url, arg, visit.queryArgs[arg]);
			}
		}

		return url;
	};

	/**
	 * Get the timestamp for a visit.
	 *
	 * @since  5.19.59
	 *
	 * @param  {Object} visit Visit object.
	 *
	 * @returns {int}         The timestamp integer.
	 */
	static getTimestamp = (visit) => {
		const ts = isNaN(parseInt(visit.timestamp)) ? 0 : parseInt(visit.timestamp);

		return ts;
	};

	/**
	 * Check if visitor has visited a specific page within a certain time frame.
	 *
	 * @since 5.21.19
	 *
	 * @param {Function} ruleCheck     The callback function to handle individual rule checks.
	 * @param {int}      ruleTimestamp The timestamp integer to filter out visits with.
	 * @param {bool}     isToday       Pass true if the operator is `today`.
	 *
	 * @returns {bool} True if the visitor visited within specified time frame.
	 */
	static hasVisited = (ruleCheck, ruleTimestamp, isToday) => {
		// Reverse the visits so that they are newest to oldest.
		const allVisits = Visits.getAll().reverse();

		// Loop through all visits
		for (var i = 0; i < allVisits.length; i++) {
			// The larger the timestamp, the more RECENT the date. If our
			// current visit's timestamp is greater than the rule's timestamp,
			// our current visit is more recent than the rule timestamp.
			const currentVisit = allVisits[i];
			const currentVisitTimestamp = Visits.getTimestamp(currentVisit);
			let datePassed = false;
			if (isToday) {
				const currentVisitStr = new Date(
					new Date(currentVisitTimestamp * 1000).setHours(0, 0, 0, 0)
				).toDateString();
				const todayStr = new Date(new Date().setHours(0, 0, 0, 0)).toDateString();
				datePassed = todayStr === currentVisitStr;
			} else {
				datePassed = currentVisitTimestamp >= ruleTimestamp;
			}

			// Dates are sorted in reverse order.
			// If the most recent visit is not a match, no others will match
			if (!datePassed) {
				return false;
			}

			// Current Visit is within threshold, now check this visit against the rule.
			const currentVisitPath = currentVisit.path || '';
			if (ruleCheck(currentVisitPath.toLowerCase())) {
				return true; // Only one match is necessary.
			}
		}

		// No visits matched.
		return false;
	};
}
